/**
 * Q-846: what the client holds after a FAILED /api/access-control/context refresh.
 *
 * A failed role read now fails the context request (500) instead of answering with empty roles. Keeping the previously
 * loaded permissions on that error would let useHasPermission / AuthGuard keep granting from stale roles (review on
 * Q-846: a user loaded as admin whose next role read errors, even after a revocation). So a failed refresh clears every
 * grant and keeps only the error: guards deny, and the header shows "Roles unavailable" (error set, no userContext).
 * The selected agency/department SCOPE is not a grant and is kept (review r2 on Q-846): erasing it would delete the
 * selection cookies and a later successful refresh would fall back to the default scope.
 */
import type { AccessControlState, AdminStatus, PermissionsState } from '@/lib/routes/types';
import type { BillingTier } from '@/lib/settings/types';

export function failedAuthContextStates(
  error: Error,
  scope: { activeAgencyId: string | null; activeDepartmentId: string | null },
): {
  permissionsState: PermissionsState;
  accessControlState: AccessControlState;
  adminStatus: AdminStatus;
} {
  return {
    permissionsState: { permissions: [], roles: [], tier: 'trial' as BillingTier, loading: false, error },
    accessControlState: {
      userContext: null,
      loading: false,
      error,
      activeAgencyId: scope.activeAgencyId,
      activeDepartmentId: scope.activeDepartmentId,
      isMasquerading: false,
      masqueradeRoleIds: [],
      masqueradeRoleNames: [],
    },
    adminStatus: {
      isAdmin: false,
      isPlatformAdmin: false,
      isTenantAdmin: false,
      isLegacyAdmin: false,
      role: null,
      loading: false,
    },
  };
}
